/* eslint-disable no-param-reassign */
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {useSelector} from 'react-redux'
import {http} from 'hiya-common/src/utils/axios'

import {CRAZY_WEEKEND_API, CRAZY_WEEKEND_RANK_LIMIT} from './constants'
import type {CrazyWeekendState, GameWeekHomepage, GameWeekRank} from './types'

const emptyHomepage: GameWeekHomepage = {
  stime: '',
  etime: '',
  countdown: 0,
  act_status: 0,
  game: {
    game_type: 0,
    game_name: '',
    game_link: '',
    videl_link: '',
    video_ratio: '1:1',
  },
  join_rewards: [],
}

const emptyRank: GameWeekRank = {
  list: [],
  own: null,
  hasMore: false,
}

const normalizeHomepage = (
  data?: Partial<GameWeekHomepage> | null
): GameWeekHomepage => {
  const game = data?.game
  return {
    stime: data?.stime || '',
    etime: data?.etime || '',
    countdown: Number(data?.countdown) || 0,
    act_status: Number(data?.act_status) || 0,
    game: {
      game_type: Number(game?.game_type) || 0,
      game_name: game?.game_name || '',
      game_link: game?.game_link || '',
      videl_link: game?.videl_link || '',
      video_ratio: game?.video_ratio === '3:4' ? '3:4' : '1:1',
      cover: game?.cover,
    },
    join_rewards: Array.isArray(data?.join_rewards) ? data.join_rewards : [],
  }
}

const normalizeRankList = (list: GameWeekRank['list'] | null | undefined) =>
  Array.isArray(list) ? list : []

export const getGameWeekHomepage = createAsyncThunk(
  'CrazyWeekend/getGameWeekHomepage',
  async (_p: void, store: any) => {
    const resp = await http.post(
      CRAZY_WEEKEND_API.homepage,
      {
        ...store.getState().env.deviceinfo,
      },
      {store, xcErrorToast: true}
    )
    return normalizeHomepage(resp?.data?.data)
  }
)

export type GetGameWeekRankParams = {
  offset?: number
  limit?: number
}

export const getGameWeekRank = createAsyncThunk(
  'CrazyWeekend/getGameWeekRank',
  async (arg: GetGameWeekRankParams, store: any) => {
    const offset = arg?.offset ?? 0
    const limit = arg?.limit ?? CRAZY_WEEKEND_RANK_LIMIT
    const resp = await http.post(
      CRAZY_WEEKEND_API.rank,
      {
        ...store.getState().env.deviceinfo,
        offset,
        limit,
      },
      {store, xcErrorToast: true}
    )
    const data = resp?.data?.data as GameWeekRank | undefined
    return {
      list: normalizeRankList(data?.list),
      own: data?.own || null,
      requestOffset: offset,
      requestLimit: limit,
    }
  }
)

const initialState: CrazyWeekendState = {
  homepage: emptyHomepage,
  rank: emptyRank,
}

export const crazyWeekendSlice = createSlice({
  name: 'CrazyWeekend',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getGameWeekHomepage.fulfilled, (state, action) => {
      state.homepage = action.payload
    })
    builder.addCase(getGameWeekRank.fulfilled, (state, action) => {
      const {payload} = action
      const incoming = payload.list
      const offset = payload.requestOffset ?? 0
      const limit = payload.requestLimit ?? CRAZY_WEEKEND_RANK_LIMIT
      state.rank = {
        list: offset > 0 ? state.rank.list.concat(incoming) : incoming,
        own: payload.own || null,
        hasMore: incoming.length >= limit,
      }
    })
  },
})

export default crazyWeekendSlice.reducer

export const useCrazyWeekendState = (): CrazyWeekendState =>
  useSelector((store: any) => store.CrazyWeekend as CrazyWeekendState) ??
  initialState
